import React from "react";
import { FormattedHTMLMessage, injectIntl } from "react-intl";
import { produce } from "immer";

import { logger } from "@jslib/shared/utils/logger";

import {
  LowIncomeStatusDefaultMessagesEnum,
  VerificationStepsEnum,
} from "../../lib/types/runtimeTypes";
import { hasFailedInstantMatch } from "../../lib/VerificationService/VerificationServiceHelpers";
import {
  type FormSelectChoice,
  type LowIncomePersonalInfoResponse,
  type LowIncomePersonalInfoViewModel,
  type LowIncomeStatus,
  type Organization,
  type State,
  type VerificationService,
  type CustomFormFieldConfig,
} from "../../lib/types/types";
import {
  getAvailableLowIncomeStatuses,
  getAvailableStateChoices,
  getFieldConfigByFieldId,
  getStatusLabel,
  handleEmailOnKeyDown,
  orgToOption,
  updateFieldValidationErrorsByFieldId,
  updateViewModelOrganization,
} from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import { BirthDateComponent as BirthDate } from "../FormFields/BirthDate/BirthDateComponent";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { StatusComponent } from "../FormFields/Status/StatusComponent";
import { LowIncomeOrganizationComponent as LowIncomeOrganization } from "../FormFields/LowIncomeOrganization/LowIncomeOrganizationComponent";
import { getOptions } from "../../options/options";
import { FormFieldCustom } from "../FormFields/FormFieldCustom/FormFieldCustom";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { PostalCodeComponent } from "../FormFields/PostalCode/PostalCodeComponent";
import { ChangeLocaleV2Component } from "../FormFields/ChangeLocale/ChangeLocaleComponent";
import { CountryComponentWrapper } from "../FormFields/Country/CountryComponentWrapper";
import { EBTCardNumberComponent } from "../FormFields/EBTCardNumber/EBTCardNumberComponent";
import { setViewModel } from "../../lib/VerificationService/ViewModel";
import { AddressComponent } from "../FormFields/Address/AddressComponent";
import { CityComponent } from "../FormFields/City/City";
import { StateSelectComponent } from "../FormFields/State/StateSelectComponent";
import { PersonalInfoWrapper } from "../PersonalInfoStepWrapper/PersonalInfoStepWrapper";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";
import { blankOrganization } from "../../lib/types/empties";
import { CaptchaComponent } from "../FormFields/Captcha/CaptchaComponent";

import { isLowIncomeVM } from "./lowIncomeTypes";

interface StepLowIncomePersonalInfoComponentProps {
  verificationService: VerificationService<LowIncomePersonalInfoViewModel>;
  intl: any;
}

type DefaultStatusMessages = {
  [key in LowIncomeStatus]: string;
};

export const defaultStatusMessages: DefaultStatusMessages = {
  SNAP_BENEFITS: "Snap Benefits Recipient",
  OTHER_GOVERNMENT_ASSISTANCE: "Other Government Assistance Recipient",
  COMMUNITY_ELIGIBILITY_PROVISION: "CEP (Community Eligibility Provision, Title 1/LAP)",
};

const StepLowIncomePersonalInfo = ({
  intl,
  verificationService,
}: StepLowIncomePersonalInfoComponentProps) => {
  const { viewModel } = verificationService;
  const options = getOptions();

  if (!isLowIncomeVM(viewModel)) {
    logger.error("Expected LowIncomePersonalInfoViewModel");
    return null;
  }

  const { fieldValidationErrors } = verificationService;
  const { verificationResponse } = verificationService;
  const failedInstantMatch = hasFailedInstantMatch(
    verificationResponse as LowIncomePersonalInfoResponse,
  );

  const locale = viewModel.localeChoice.value;

  const selectedStatus = viewModel.statuses[0] ? viewModel.statuses[0] : undefined;
  const isSelectedStatusSnapBenefits = selectedStatus === "SNAP_BENEFITS";
  const updateLowIncomeViewModel = (key: keyof LowIncomePersonalInfoViewModel, value: any) => {
    if (!isLowIncomeVM(viewModel)) {
      logger.error("Expected LowIncomePersonalInfoViewModel");
      return;
    }
    const nextState: LowIncomePersonalInfoViewModel = produce(
      viewModel,
      (draft: LowIncomePersonalInfoViewModel) => {
        draft[key] = value;
      },
    );
    verificationService.updateViewModel(nextState);
  };

  React.useEffect(() => {
    if (isSelectedStatusSnapBenefits) {
      const updatedViewModel = { fieldsToSkipValidation: ["organization"] };
      setViewModel(updatedViewModel);
    }
  }, [isSelectedStatusSnapBenefits]);

  return (
    <PersonalInfoWrapper
      verificationService={verificationService}
      step={verificationResponse.currentStep}
    >
      <PersonalInfoHeader
        verificationService={verificationService}
        classNameIdentifier="low-income"
      />
      <ChangeLocaleV2Component verificationService={verificationService} />
      <CountryComponentWrapper
        verificationService={verificationService}
        disabled={getFieldConfigByFieldId("country")?.disabled}
      />
      <StatusComponent
        segmentId="low-income"
        value={{
          value: viewModel.statuses ? viewModel.statuses[0] : "",
          label: viewModel.statuses
            ? getStatusLabel(intl, viewModel.statuses[0], LowIncomeStatusDefaultMessagesEnum)
            : "",
        }}
        isErrored={!!fieldValidationErrors.statuses}
        disabled={getFieldConfigByFieldId("status")?.disabled}
        options={getAvailableLowIncomeStatuses(
          intl,
          (verificationResponse as LowIncomePersonalInfoResponse).availableStatuses,
        )}
        onChange={(status: FormSelectChoice<LowIncomeStatus>) => {
          const updatedStatuses = status ? [status.value as LowIncomeStatus] : [];
          const nextState: LowIncomePersonalInfoViewModel = produce(
            viewModel,
            (draft: LowIncomePersonalInfoViewModel) => {
              if (viewModel.statuses[0] !== updatedStatuses[0]) {
                draft.organization = blankOrganization.organization; // Clear out the org b/c they're of different kind
              }
              draft.statuses = updatedStatuses;
            },
          );
          verificationService.updateViewModel(nextState);
          updateFieldValidationErrorsByFieldId(
            "statuses",
            status ? status.value : "",
            verificationService,
          );
        }}
      />
      {selectedStatus === "SNAP_BENEFITS" && (
        <EBTCardNumberComponent
          value={viewModel.ebtCardNumber}
          isErrored={!!fieldValidationErrors.ebtCardNumber}
          disabled={getFieldConfigByFieldId("ebtCardNumber")?.disabled}
          onChange={(newValue) => {
            updateLowIncomeViewModel("ebtCardNumber", newValue);
            updateFieldValidationErrorsByFieldId("ebtCardNumber", newValue, verificationService);
          }}
        />
      )}
      {(selectedStatus === "OTHER_GOVERNMENT_ASSISTANCE" ||
        selectedStatus === "COMMUNITY_ELIGIBILITY_PROVISION") && (
        <LowIncomeOrganization
          isErrored={!!fieldValidationErrors.organization}
          onChange={(choice: Organization) => {
            updateViewModelOrganization(choice, verificationService);
            updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
          }}
          value={orgToOption(verificationService.viewModel.organization)}
          verificationService={verificationService}
          isRequired
          disabled={getFieldConfigByFieldId("organization")?.disabled}
        />
      )}
      <div className="sid-names">
        <FirstName
          value={viewModel.firstName}
          isErrored={!!fieldValidationErrors.firstName}
          disabled={getFieldConfigByFieldId("firstName")?.disabled}
          onChange={(newValue) => {
            updateLowIncomeViewModel("firstName", newValue);
            updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
          }}
        />
        <LastName
          value={viewModel.lastName}
          isErrored={!!fieldValidationErrors.lastName}
          disabled={getFieldConfigByFieldId("lastName")?.disabled}
          onChange={(newValue) => {
            updateLowIncomeViewModel("lastName", newValue);
            updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
          }}
        />
      </div>
      <BirthDate
        errorId={fieldValidationErrors.birthDate}
        locale={locale}
        disabled={getFieldConfigByFieldId("birthDate")?.disabled}
        onChange={(newValue) => {
          updateLowIncomeViewModel("birthDate", newValue);
          updateFieldValidationErrorsByFieldId("birthDate", newValue, verificationService);
        }}
        value={viewModel.birthDate}
      />
      <AddressComponent
        isErrored={!!fieldValidationErrors.address1}
        errorId={fieldValidationErrors.address1}
        disabled={getFieldConfigByFieldId("address1")?.disabled}
        onChange={(newValue) => {
          updateLowIncomeViewModel("address1", newValue);
          updateFieldValidationErrorsByFieldId("address1", newValue, verificationService);
        }}
        value={viewModel.address1}
        isRequired
        explanation={
          <FormattedHTMLMessage id="addressExplanation" defaultMessage="Use your home address" />
        }
      />
      <div className="sid-address-extra-fields sid-thirds">
        <CityComponent
          isErrored={!!fieldValidationErrors.city}
          errorId={fieldValidationErrors.city}
          onChange={(newValue) => {
            updateLowIncomeViewModel("city", newValue);
            updateFieldValidationErrorsByFieldId("city", newValue, verificationService);
          }}
          value={viewModel.city}
          isRequired
          disabled={getFieldConfigByFieldId("city")?.disabled}
        />
        <StateSelectComponent
          isRequired
          options={getAvailableStateChoices(verificationService.programTheme, intl)}
          value={viewModel.state || undefined}
          isErrored={Boolean(fieldValidationErrors.state)}
          disabled={getFieldConfigByFieldId("state")?.disabled}
          onChange={async (stateChoice: FormSelectChoice<State, string>) => {
            const stateValue = stateChoice ? stateChoice.value : undefined;
            updateLowIncomeViewModel("state", stateValue);
            updateFieldValidationErrorsByFieldId("state", stateValue, verificationService);
          }}
        />
        <PostalCodeComponent
          isErrored={!!fieldValidationErrors.postalCode}
          onChange={(newValue) => {
            updateLowIncomeViewModel("postalCode", newValue);
            updateFieldValidationErrorsByFieldId("postalCode", newValue, verificationService);
          }}
          value={viewModel.postalCode}
          disabled={getFieldConfigByFieldId("postalCode")?.disabled}
        />
      </div>
      <Email
        value={viewModel.email}
        isErrored={!!fieldValidationErrors.email}
        explanation={<FormattedHTMLMessage id="emailExplanation" defaultMessage=" " />}
        onChange={(newValue) => {
          updateLowIncomeViewModel("email", newValue);
          updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
        }}
        onKeyDown={(event) => handleEmailOnKeyDown(event)}
        disabled={getFieldConfigByFieldId("email")?.disabled}
      />
      {verificationService.programTheme.isSmsNotifierConfigured ||
      verificationService.programTheme.smsLoopEnabled ? (
        <PhoneNumber
          isRequired={!!verificationService.programTheme.smsLoopEnabled}
          value={viewModel.phoneNumber}
          isErrored={!!fieldValidationErrors.phoneNumber}
          onChange={(newValue) => {
            updateLowIncomeViewModel("phoneNumber", newValue);
            updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
          }}
          selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
          disabled={getFieldConfigByFieldId("phoneNumber")?.disabled}
        />
      ) : null}
      {(options.customFormFields || []).map((config: CustomFormFieldConfig) => (
        <FormFieldCustom config={config} verificationService={verificationService} />
      ))}{" "}
      <MarketConsentWrapper
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.marketConsentValue}
        onChange={(newValue) => {
          updateLowIncomeViewModel("metadata", {
            ...verificationService.viewModel.metadata,
            marketConsentValue: newValue,
          });
          updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
        }}
        viewModel={viewModel}
        disabled={getFieldConfigByFieldId("marketConsentValue")?.disabled}
      />
      <CaptchaComponent
        verificationService={verificationService}
        onChange={(newValue) => {
          updateLowIncomeViewModel("captchaToken", newValue);
          updateFieldValidationErrorsByFieldId("captchaToken", newValue, verificationService);
        }}
        isErrored={!!fieldValidationErrors.captchaToken}
      />
      <PersonalInfoStepSubmitButton
        step={VerificationStepsEnum.collectLowIncomePersonalInfo}
        verificationService={verificationService}
        viewModel={viewModel}
        failedInstantMatch={failedInstantMatch}
        buttonText={
          <FormattedHTMLMessage
            id="step.personalInfo.submitButtonText"
            defaultMessage="Verify Status"
          />
        }
      />
    </PersonalInfoWrapper>
  );
};

export const StepLowIncomePersonalInfoComponent = injectIntl(StepLowIncomePersonalInfo);
